"use client";

import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, Download } from "lucide-react";
import type { ColumnDefinition } from "./types";
import { downloadSampleCsv } from "./utils";

interface SampleTemplateCardProps<T> {
  columns: ColumnDefinition<T>[];
  generateSampleCsv: () => string;
}

/**
 * Sample template card component for CSV import
 */
export function SampleTemplateCard<T extends Record<string, any>>({
  columns,
  generateSampleCsv,
}: SampleTemplateCardProps<T>) {
  const handleDownload = () => {
    const sampleCsv = generateSampleCsv();
    downloadSampleCsv(sampleCsv);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Sample Template
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Download the sample CSV template to see the required format and column headers.
          </p>
          <div className="bg-card-50 p-4 rounded-lg">
            <h4 className="font-medium text-sm mb-2">Columns:</h4>
            <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
              {columns.map((col) => (
                <li key={String(col.key)}>
                  <strong>{String(col.key)}</strong> - {col.description}{" "}
                  {col.required && "(required)"}
                </li>
              ))}
            </ul>
          </div>
          <Button onClick={handleDownload} variant="outline" className="gap-2">
            <Download className="h-4 w-4" />
            Download Template
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
